import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client.js";
import { useI18n } from "../i18n/I18nContext.jsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import StatusBadge from "../components/StatusBadge.jsx";
import "../styles/Reviews.css";

function fmtDate(d) {
  return d ? new Date(d).toLocaleString("en-IN") : "—";
}

// Page for officers to view their submitted answers
export default function SubmittedAnswers() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  // Persist the active tab in the URL (?tab=CLARIFICATION) so refresh keeps
  // the user on the same tab and the URL is shareable.
  const filter = searchParams.get("tab") || "";
  const setFilter = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key) next.set("tab", key);
    else next.delete("tab");
    setSearchParams(next, { replace: true });
  };
  const [clarReplyText, setClarReplyText] = useState({}); // reply.id -> text
  const [clarFiles, setClarFiles] = useState({}); // reply.id -> File[]
  const [clarDocType, setClarDocType] = useState({}); // reply.id -> doc_type
  const [clarBusy, setClarBusy] = useState(null); // reply.id currently submitting
  const [clarMsg, setClarMsg] = useState("");

  const submitClarificationReply = async (reply) => {
    const text = (clarReplyText[reply.id] || "").trim();
    if (!text) return;
    setClarBusy(reply.id);
    try {
      // 1. Create the reply
      const r = await api.post(`/paras/${reply.para}/submit_reply/`, {
        explanation: text,
      });
      const newReplyId = r.data.id;
      // 2. Upload each attached evidence file, linked to the new reply
      const files = clarFiles[reply.id] || [];
      const docType = clarDocType[reply.id] || "LETTER";
      for (const file of files) {
        const fd = new FormData();
        fd.append("para", reply.para);
        fd.append("reply", newReplyId);
        fd.append("file", file);
        fd.append("doc_type", docType);
        await api.post("/evidence/", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      // 3. Clear inputs for this card
      const clear = (setter) =>
        setter((prev) => {
          const next = { ...prev };
          delete next[reply.id];
          return next;
        });
      clear(setClarReplyText);
      clear(setClarFiles);
      clear(setClarDocType);
      setClarMsg(t("clarificationSuccess"));
      setTimeout(() => setClarMsg(""), 3000);
      load();
    } catch (e) {
      window.alert(
        e.response?.data?.detail ||
          e.response?.data?.explanation ||
          t("submitError")
      );
    } finally {
      setClarBusy(null);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = { page_size: 100 };
    if (filter) params.review_status = filter;
    api
      .get("/replies/", { params })
      .then((r) => setReplies(r.data.results || r.data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep only the latest reply per (para, officer) — older attempts for the
  // same para become stale once the officer resubmits, so showing them as
  // separate cards on the officer's own page just looks like duplicates.
  const visibleReplies = useMemo(() => {
    const map = new Map();
    for (const r of replies) {
      const key = `${r.para}-${r.officer ?? "_"}`;
      const existing = map.get(key);
      const rTime = r.submitted_at ? new Date(r.submitted_at).getTime() : 0;
      const eTime = existing?.submitted_at
        ? new Date(existing.submitted_at).getTime()
        : 0;
      if (!existing || rTime > eTime) {
        map.set(key, r);
      }
    }
    return Array.from(map.values()).sort(
      (a, b) =>
        new Date(b.submitted_at || 0).getTime() -
        new Date(a.submitted_at || 0).getTime()
    );
  }, [replies]);

  return (
    <div className="page-reviews">
      <div className="page-header">
        <div>
          <h1>{t("submittedAnswers")}</h1>
        </div>
      </div>

      {clarMsg && (
        <div
          className="badge green"
          style={{
            marginBottom: 12, display: "block", padding: "10px 16px",
          }}
        >
          {clarMsg}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="section-tabs" data-testid="manage-filter-tabs">
        {[
          { key: "", label: t("all") },
          { key: "SUBMITTED", label: t("pendingReview") },
          { key: "CLARIFICATION", label: t("clarification") },
          { key: "ACCEPTED", label: t("statusACCEPTED") },
          { key: "REJECTED", label: t("statusREJECTED") },
        ].map((tab) => (
          <div
            key={tab.key}
            className={"section-tab" + (filter === tab.key ? " active" : "")}
            onClick={() => setFilter(tab.key)}
            data-testid={`tab-${tab.key || "all"}`}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* Replies List */}
      {loading ? (
        <div className="center-screen">
          <div className="spinner"></div>
        </div>
      ) : visibleReplies.length === 0 ? (
        <div className="card empty-state">
          <p>{t("noRepliesStatus")}</p>
        </div>
      ) : (
        <div className="replies-grid">
          {visibleReplies.map((r) => (
            <div key={r.id} className="reply-card" data-testid={`reply-card-${r.id}`}>
              <div className="reply-header">
                <div>
                  <h3 className="reply-para">
                    {r.para_no} <span className="muted">· {r.para_audit_year}</span>
                  </h3>
                  <p className="reply-title muted">{r.para_title}</p>
                </div>
                <StatusBadge status={r.review_status} />
              </div>

              <div className="reply-meta">
                <span><strong>{t("by")}:</strong> {r.officer_name || "—"}</span>
                <span><strong>{t("submitted")}:</strong> {fmtDate(r.submitted_at)}</span>
              </div>

              <div className="reply-explanation">
                <strong>{t("explanation")}:</strong>
                <p>{r.explanation}</p>
              </div>

              {r.evidence && r.evidence.length > 0 && (
                <div className="reply-evidence">
                  <strong>{t("evidence")} ({r.evidence.length}):</strong>
                  <div className="evidence-list">
                    {r.evidence.map((ev) => (
                      <a
                        key={ev.id}
                        href={ev.file}
                        target="_blank"
                        rel="noreferrer"
                        className="tag-pill"
                        onClick={(e) => e.stopPropagation()}
                      >
                        📎 {ev.doc_type_display} {ev.description ? `· ${ev.description}` : ""}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {r.review_remarks && (
                <div className="review-remarks">
                  <strong>{t("reviewRemarks")}:</strong>
                  <p>{r.review_remarks}</p>
                  <small className="muted">
                    {t("reviewedBy")} {r.reviewed_by_name} · {fmtDate(r.reviewed_at)}
                  </small>
                </div>
              )}

              {r.review_status === "CLARIFICATION" && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    background: "#f0f9ff",
                    border: "1px solid #7dd3fc",
                    borderRadius: 6,
                  }}
                >
                  <strong style={{ display: "block", marginBottom: 6, color: "#075985" }}>
                    {t("replyToClarification")}
                  </strong>
                  <textarea
                    value={clarReplyText[r.id] || ""}
                    onChange={(e) =>
                      setClarReplyText((prev) => ({
                        ...prev,
                        [r.id]: e.target.value,
                      }))
                    }
                    placeholder={t("clarificationPlaceholder")}
                    rows={4}
                    style={{
                      width: "100%",
                      padding: 8,
                      borderRadius: 4,
                      border: "1px solid #bae6fd",
                      fontFamily: "inherit",
                      fontSize: 13,
                      resize: "vertical",
                    }}
                    data-testid={`clarification-reply-input-${r.id}`}
                  />

                  {/* Evidence upload — same flow as the main submission form */}
                  <div
                    style={{
                      marginTop: 10,
                      paddingTop: 10,
                      borderTop: "1px dashed #bae6fd",
                    }}
                  >
                    <strong style={{ display: "block", fontSize: 12, color: "#075985", marginBottom: 6 }}>
                      {t("attachEvidence")}
                    </strong>
                    <select
                      value={clarDocType[r.id] || "LETTER"}
                      onChange={(e) =>
                        setClarDocType((prev) => ({
                          ...prev,
                          [r.id]: e.target.value,
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: 6,
                        borderRadius: 4,
                        border: "1px solid #bae6fd",
                        fontSize: 13,
                        background: "#fff",
                        marginBottom: 8,
                      }}
                    >
                      <option value="LETTER">{t("explanationLetter")}</option>
                      <option value="PHOTO">{t("photograph")}</option>
                      <option value="WORK_ORDER">{t("workOrder")}</option>
                      <option value="BILL">{t("billVoucher")}</option>
                      <option value="MEASUREMENT">{t("measurementBook")}</option>
                      <option value="PROCEEDINGS">{t("proceedingsCopy")}</option>
                      <option value="RESOLUTION">{t("councilResolution")}</option>
                      <option value="OTHER">{t("other")}</option>
                    </select>
                    <input
                      type="file"
                      multiple
                      onChange={(e) =>
                        setClarFiles((prev) => ({
                          ...prev,
                          [r.id]: Array.from(e.target.files || []),
                        }))
                      }
                      style={{ fontSize: 12 }}
                      data-testid={`clarification-files-${r.id}`}
                    />
                    {(clarFiles[r.id] || []).length > 0 && (
                      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                        {(clarFiles[r.id] || []).length} file(s) selected
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                      marginTop: 10,
                    }}
                  >
                    <button
                      className="btn btn-sm btn-primary"
                      disabled={
                        clarBusy === r.id ||
                        !(clarReplyText[r.id] || "").trim()
                      }
                      onClick={() => submitClarificationReply(r)}
                      data-testid={`submit-clarification-${r.id}`}
                    >
                      {clarBusy === r.id ? t("submitting") : t("submitReply")}
                    </button>
                  </div>
                </div>
              )}

              <div className="reply-actions">
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => navigate(`/paras/${r.para}`)}
                  data-testid={`view-para-${r.id}`}
                >
                  {t("viewPara")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
